/**
 * Fansly account settings the console shows (ADR 0036 decision 6):
 * notification alerts, blocked locations, watermark, who can see the
 * timeline, and — read-only — login sessions and third-party connections.
 * Shapes and write bodies from Fansly's app (settings routes,
 * NotificationService, IgnoreService, MediaService, SessionManagement).
 */
import { asList, asObj, num, str, toMs, unwrap, type Raw } from '../data'

// ── Notification alerts ──────────────────────────────────────────────────────

/** Provider types: 1 e-mail, 2 push. */
export const PROVIDER_ORDER = [2, 1]

export interface AlertSetting {
  notificationAlertType: number
  label: string
  creatorOnly: boolean
  /** The alert is sent (its "muted" bit 1 is off). */
  on: boolean
  /** The stored setting, if the account has one (else Fansly's default applies). */
  stored: Raw | null
  defaultFlags: number
}

export interface AlertProvider {
  id: string
  type: number
  settings: AlertSetting[]
  raw: Raw
}

function storedSettings(p: Raw): Raw[] {
  return asList(p.settings)
}

/** GET /crm/settings/notifications → providers, each with its alert toggles. */
export function parseAlertProviders(payload: unknown): AlertProvider[] {
  const u = unwrap(payload)
  const list = Array.isArray(u) ? asList(u) : asList(asObj(u).providers ?? u)
  return list
    .map((p) => {
      const stored = storedSettings(p)
      const settings = asList(p.availableSettings).map((a) => {
        const type = num(a.notificationAlertType)
        const mine = stored.find((s) => num(s.notificationAlertType) === type) ?? null
        const flags = mine ? num(mine.flags) : num(a.defaultFlags)
        return { notificationAlertType: type, label: str(a.label), creatorOnly: a.creatorOnlyAlert === true, on: (flags & 1) !== 1, stored: mine, defaultFlags: num(a.defaultFlags) }
      })
      return { id: str(p.id), type: num(p.type), settings, raw: p }
    })
    .filter((p) => p.id)
    .sort((a, b) => {
      const ia = PROVIDER_ORDER.indexOf(a.type)
      const ib = PROVIDER_ORDER.indexOf(b.type)
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
    })
}

/** The body Fansly posts to toggle one alert (its stored setting, else a new one, with bit 1 = muted). */
export function alertToggleBody(provider: AlertProvider, s: AlertSetting, on: boolean): Record<string, unknown> {
  const base = s.stored ?? { providerId: provider.id, notificationAlertType: s.notificationAlertType, flags: s.defaultFlags }
  const flags = num(base.flags)
  return { ...base, flags: on ? flags & ~1 : flags | 1 }
}

/** Apply a toggle (or Fansly's answer) to the providers in place. */
export function withAlert(providers: AlertProvider[], providerId: string, type: number, on: boolean, stored?: Raw | null): AlertProvider[] {
  return providers.map((p) =>
    p.id !== providerId ? p : { ...p, settings: p.settings.map((s) => (s.notificationAlertType !== type ? s : { ...s, on, stored: stored === undefined ? s.stored : stored })) },
  )
}

// ── Blocked locations ────────────────────────────────────────────────────────

/** Fansly's country list (ISO 3166 alpha-2), names come from Intl. */
export const COUNTRY_CODES =
  'AF AL DZ AS AD AO AI AQ AG AR AM AW AU AT AZ BS BH BD BB BY BE BZ BJ BM BT BO BQ BA BW BV BR IO BN BG BF BI CV KH CM CA KY CF TD CL CN CX CC CO KM CD CG CK CR HR CU CW CY CZ CI DK DJ DM DO EC EG SV GQ ER EE SZ ET FK FO FJ FI FR GF PF TF GA GM GE DE GH GI GR GL GD GP GU GT GG GN GW GY HT HM VA HN HK HU IS IN ID IR IQ IE IM IL IT JM JP JE JO KZ KE KI KR XK KW KG LA LV LB LS LR LY LI LT LU MO MG MW MY MV ML MT MH MQ MR MU YT MX FM MD MC MN ME MS MA MZ MM NA NR NP NL NC NZ NI NE NG NU NF MP NO OM PK PW PS PA PG PY PE PH PN PL PT PR QA MK RO RU RW RE BL SH KN LC MF PM VC WS SM ST SA SN RS SC SL SG SX SK SI SB SO ZA GS SS ES LK SD SR SJ SE CH SY TW TJ TZ TH TL TG TK TO TT TN TR TM TC TV UG UA AE GB US UY UZ VU VE VN VG VI WF EH YE ZM ZW AX UM'.split(' ')

export function countryName(code: string, lang = 'en'): string {
  if (!code) return ''
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

export interface BlockedLocation {
  id: string
  city: string
  state: string
  stateCode: string
  country: string
  countryCode: string
  raw: Raw
}

/** GET /crm/settings/privacy/locations → blocked locations. */
export function parseLocations(payload: unknown): BlockedLocation[] {
  const u = unwrap(payload)
  const list = Array.isArray(u) ? asList(u) : asList(asObj(u).locations ?? u)
  return list
    .map((r) => {
      let d = asObj(r.locationData)
      if (!Object.keys(d).length && typeof r.locationDataString === 'string') {
        try {
          d = asObj(JSON.parse(r.locationDataString))
        } catch {
          d = {}
        }
      }
      return { id: str(r.id), city: str(d.city), state: str(d.state), stateCode: str(d.stateCode), country: str(d.country), countryCode: str(d.countryCode), raw: r }
    })
    .filter((l) => l.id)
}

/** "Austin, TX · United States" in the viewer's language. */
export function locationLabel(l: Pick<BlockedLocation, 'city' | 'state' | 'stateCode' | 'country' | 'countryCode'>, lang = 'en'): string {
  const place = [l.city, l.stateCode || l.state].filter(Boolean).join(', ')
  const country = l.countryCode ? countryName(l.countryCode, lang) : l.country
  return [place, country].filter(Boolean).join(' · ')
}

/**
 * The body of locations/add: Fansly's page sends the place it resolved
 * (`{locationData:{city, state, country, countryCode, stateCode}}`).
 */
export function addLocationBody(countryCode: string, region = '', city = ''): Record<string, unknown> {
  const r = region.trim()
  const isCode = /^[A-Za-z0-9]{1,3}$/.test(r)
  return {
    locationData: {
      city: city.trim(),
      state: isCode ? '' : r,
      country: countryName(countryCode, 'en'),
      countryCode: countryCode.toUpperCase(),
      stateCode: isCode ? r.toUpperCase() : '',
    },
  }
}

// ── Watermark ────────────────────────────────────────────────────────────────

/** GET /crm/media/watermark → the watermark text, or null when none is set. */
export function parseWatermark(payload: unknown): string | null {
  const u = unwrap(payload)
  if (typeof u === 'string') return u
  const o = asObj(u)
  return typeof o.watermark === 'string' ? o.watermark : null
}

// ── Sessions (read-only) ─────────────────────────────────────────────────────

export interface LoginSession {
  id: string
  ip: string
  lastUsed: number
  location: string
  status: number
  /** Fansly marks it as the session asking (rare from the gateway). */
  current: boolean
}

/** GET /crm/settings/sessions → sessions, most recently used first. */
export function parseLoginSessions(payload: unknown): LoginSession[] {
  const u = unwrap(payload)
  const list = Array.isArray(u) ? asList(u) : asList(asObj(u).sessions ?? u)
  return list
    .map((r) => {
      const d = asObj(r.locationData)
      return {
        id: str(r.id),
        ip: str(r.ip) || str(d.ip),
        lastUsed: toMs(r.lastUsed),
        location: [d.countryCode, d.stateCode, d.city].map(str).filter((x) => x.trim()).join(', '),
        status: num(r.status),
        current: r.current === true || r.isCurrent === true || r.active === true,
      }
    })
    .filter((s) => s.id)
    .sort((a, b) => b.lastUsed - a.lastUsed)
}

/** Fansly's own session list asks these statuses. */
export const SESSION_STATUSES = '0, 2'

// ── Connections (read-only) ──────────────────────────────────────────────────

export interface Connection {
  providerId: string
  name: string
  /** What identifies each connected account (a handle, or a masked e-mail). */
  accounts: string[]
}

/** "j•••@example.com": connected e-mails are not shown in full (ADR 0036). */
export function maskEmail(email: string): string {
  const [user, domain] = email.split('@')
  if (!domain) return email ? `${email.slice(0, 1)}•••` : ''
  return `${(user ?? '').slice(0, 1)}•••@${domain}`
}

/** GET /crm/settings/connections → providers with their connected accounts. */
export function parseConnections(payload: unknown): Connection[] {
  const u = unwrap(payload)
  const list = Array.isArray(u) ? asList(u) : asList(asObj(u).providers ?? u)
  return list
    .map((p) => ({
      providerId: str(p.providerId) || str(p.id),
      name: str(p.name),
      accounts: asList(p.connectedAccounts)
        .map((a) => (str(a.providerUsername) ? `@${str(a.providerUsername)}` : maskEmail(str(a.providerEmail))) || str(a.providerAccountId))
        .filter(Boolean),
    }))
    .filter((c) => c.providerId || c.name)
}

// ── Two-factor authentication ──────────────────────────────────────────────

/** Fansly's account flag TWOFA_ENABLED (account/me `flags`). */
const TWOFA_ENABLED = 32

export const twoFaOn = (account: Raw): boolean => (num(account.flags) & TWOFA_ENABLED) === TWOFA_ENABLED

/** What twofa/create answers: the TOTP secret and Fansly's QR (an otpauth URI or an image). */
export interface TwoFaSecret {
  secret: string
  qrCodeUrl: string
}

/** How to draw the QR: Fansly's own image, else an otpauth URI encoded here. */
export function twoFaQr(s: TwoFaSecret, username: string): { kind: 'image' | 'uri'; value: string } {
  if (/^(https?:|data:image\/)/i.test(s.qrCodeUrl)) return { kind: 'image', value: s.qrCodeUrl }
  if (/^otpauth:/i.test(s.qrCodeUrl)) return { kind: 'uri', value: s.qrCodeUrl }
  const label = encodeURIComponent(`Fansly:${username || 'account'}`)
  return { kind: 'uri', value: `otpauth://totp/${label}?secret=${encodeURIComponent(s.secret)}&issuer=Fansly` }
}
